import axios from 'axios';
import { Channel, YouTubeSettings, TikTokSettings, Video } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('channelId');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth endpoints
export const authApi = {
  login: () => api.get('/auth/login'),
  getMe: (channelId: number) => api.get(`/auth/me?channel_id=${channelId}`),
};

// Channel endpoints
export const channelApi = {
  getChannel: (channelId: number): Promise<{ data: Channel }> =>
    api.get(`/api/channels/${channelId}`),

  getYouTubeSettings: (channelId: number): Promise<{ data: YouTubeSettings }> =>
    api.get(`/api/channels/${channelId}/youtube`),

  updateYouTubeSettings: (channelId: number, settings: Partial<YouTubeSettings>): Promise<{ data: YouTubeSettings }> =>
    api.patch(`/api/channels/${channelId}/youtube`, settings),

  getWidgetUrl: (channelId: number): Promise<{ data: { widget_url: string } }> =>
    api.get(`/api/channels/${channelId}/widget-url`),

  regenerateWidgetToken: (channelId: number): Promise<{ data: { widget_url: string, message: string } }> =>
    api.post(`/api/channels/${channelId}/widget-token/regenerate`),
};

// YouTube integration endpoints
export const youtubeApi = {
  getQueue: (channelId: number): Promise<{ data: Video[] }> =>
    api.get(`/api/channels/integrations/youtube/${channelId}/queue`),

  getCurrentVideo: (channelId: number): Promise<{ data: { current_video: Video | null } }> =>
    api.get(`/api/channels/integrations/youtube/${channelId}/queue/current`),

  addVideo: (channelId: number, url: string) =>
    api.post(`/api/channels/integrations/youtube/${channelId}/queue`, { url }),

  skipToNext: (channelId: number) =>
    api.post(`/api/channels/integrations/youtube/${channelId}/queue/next`),

  cancelVideo: (channelId: number, videoId: number) =>
    api.delete(`/api/channels/integrations/youtube/${channelId}/queue/${videoId}`),

  clearQueue: (channelId: number) =>
    api.post(`/api/channels/integrations/youtube/${channelId}/queue/clear`),

  getStats: (channelId: number) =>
    api.get(`/api/channels/integrations/youtube/${channelId}/stats`),
};


// TikTok integration endpoints
export const tiktokApi = {
  getQueue: (channelId: number): Promise<{ data: Video[] }> =>
    api.get(`/api/channels/integrations/tiktok/${channelId}/queue`),

  getCurrentVideo: (channelId: number): Promise<{ data: { current_video: Video | null } }> =>
    api.get(`/api/channels/integrations/tiktok/${channelId}/queue/current`),

  addVideo: (channelId: number, url: string) =>
    api.post(`/api/channels/integrations/tiktok/${channelId}/queue`, { url }),

  skipToNext: (channelId: number) =>
    api.post(`/api/channels/integrations/tiktok/${channelId}/queue/next`),

  cancelVideo: (channelId: number, videoId: number) =>
    api.delete(`/api/channels/integrations/tiktok/${channelId}/queue/${videoId}`),

  clearQueue: (channelId: number) =>
    api.post(`/api/channels/integrations/tiktok/${channelId}/queue/clear`),

  getSettings: (channelId: number): Promise<{ data: TikTokSettings }> =>
    api.get(`/api/channels/integrations/tiktok/${channelId}/settings`),

  updateSettings: (channelId: number, settings: Partial<TikTokSettings>): Promise<{ data: TikTokSettings }> =>
    api.patch(`/api/channels/integrations/tiktok/${channelId}/settings`, settings),
};

export default api;